import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as loginApi, logout as logoutApi } from '@/api/modules/auth'
import type { LoginParams, LoginResult } from '@/api/modules/auth'
import { storage } from '@/utils/storage'

// 项目开关：默认赋予 admin 权限（后续后端支持角色接口后改为 false）
const ADMIN_BY_DEFAULT = true

export interface UserInfo {
  id: number
  username: string
  nickname: string
  avatar?: string
  roles: string[]
  permissions: string[]
}

const TOKEN_KEY = 'token'
const USER_KEY = 'userInfo'

function mapUserInfo(result: LoginResult): UserInfo {
  if (ADMIN_BY_DEFAULT) {
    return {
      id: result.id,
      username: result.user_name,
      nickname: result.user_name,
      avatar: undefined,
      roles: ['admin'],
      permissions: ['*'],
    }
  }
  return {
    id: result.id,
    username: result.user_name,
    nickname: result.user_name,
    avatar: undefined,
    roles: [],
    permissions: [],
  }
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>(storage.get<string>(TOKEN_KEY) ?? '')
  const userInfo = ref<UserInfo | null>(storage.get<UserInfo>(USER_KEY) ?? null)

  const isLoggedIn = computed(() => !!token.value)
  const roles = computed(() => userInfo.value?.roles ?? [])
  const permissions = computed(() => userInfo.value?.permissions ?? [])

  async function login(params: LoginParams) {
    const data = await loginApi(params)
    token.value = data.token
    storage.set(TOKEN_KEY, data.token)

    const info = mapUserInfo(data)
    userInfo.value = info
    storage.set(USER_KEY, info)
  }

  async function logout() {
    try {
      await logoutApi()
    } finally {
      token.value = ''
      userInfo.value = null
      storage.remove(TOKEN_KEY)
      storage.remove(USER_KEY)
    }
  }

  return { token, userInfo, isLoggedIn, roles, permissions, login, logout }
})
